import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthResponse } from '../types';

interface AuthContextType {
  user: AuthResponse | null;
  token: string | null;
  login: (data: AuthResponse) => void;
  logout: () => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// JWT'den expiry'yi oku (saniye cinsinden timestamp)
const getTokenExpiry = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // ms'e çevir
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthResponse | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      // Token expire olmuş mu kontrol et
      const expiry = getTokenExpiry(savedToken);
      if (expiry && expiry < Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return;
      }
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Her dakika token süresini kontrol et, dolmuşsa otomatik logout
  useEffect(() => {
    if (!token) return;
    const checkExpiry = () => {
      const expiry = getTokenExpiry(token);
      if (expiry && expiry < Date.now()) {
        alert('Oturum süreniz doldu, tekrar giriş yapmanız gerekiyor.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        window.location.href = '/login';
      } else if (expiry && expiry - Date.now() < 5 * 60 * 1000) {
        // Son 5 dakikaya girince uyarı (sadece ilk seferde)
        const warned = sessionStorage.getItem('expiry-warned');
        if (!warned) {
          sessionStorage.setItem('expiry-warned', '1');
          alert('Oturumunuz 5 dakika içinde sona erecek. Lütfen işinizi kaydedin ve tekrar giriş yapın.');
        }
      }
    };
    checkExpiry();
    const interval = setInterval(checkExpiry, 60 * 1000); // her dakika
    return () => clearInterval(interval);
  }, [token]);

  const login = (data: AuthResponse) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data));
    sessionStorage.removeItem('expiry-warned');
    setToken(data.token);
    setUser(data);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('expiry-warned');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAdmin: user?.role === 'ADMIN' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
